import { useCallback, useEffect, useRef, useState } from "react";
import type { BarSpan } from "./DensityChart.lib";
import type { DensityChartBucketUnit } from "./densityBuckets.lib";
import { isSameSpan } from "./windowSpan.lib";

export type WindowDragPhase = "idle" | "dragging" | "settling";

const SETTLE_MS = 200;

const isSameDraft = (a: BarSpan | null, b: BarSpan | null) =>
    a === null || b === null ? a === b : isSameSpan(a, b);

interface UseDragDraftParams {
    unit: DensityChartBucketUnit;
    /** Gets spans in bars from the epoch, like the draft, so a redrawn strip doesn't hide a change. */
    onPreview?: (span: BarSpan | null) => void;
}

/**
 * A dragged window's draft span and phase. `settle` keeps the snapped draft
 * for 200ms ("settling", so CSS can tween the snap), then drops it. A draft
 * started in another unit reads as none and "idle", e.g. once a drop zooms
 * out. `updatePreview` keeps the span a release would land on as `preview`
 * and tells `onPreview` only when it changes.
 */
export function useDragDraft({ unit, onPreview }: UseDragDraftParams) {
    const [draft, setDraft] = useState<BarSpan | null>(null);
    const [phase, setPhase] = useState<WindowDragPhase>("idle");
    const [draftUnit, setDraftUnit] = useState(unit);
    const [preview, setPreview] = useState<BarSpan | null>(null);
    const draftRef = useRef<BarSpan | null>(null);
    const previewRef = useRef<BarSpan | null>(null);

    const updateDraft = useCallback((next: BarSpan | null) => {
        if (!isSameDraft(next, draftRef.current)) {
            draftRef.current = next;
            setDraft(next);
        }
    }, []);

    useEffect(() => {
        if (phase !== "settling") {
            return undefined;
        }
        const timeout = window.setTimeout(() => {
            setPhase("idle");
            updateDraft(null);
        }, SETTLE_MS);
        return () => window.clearTimeout(timeout);
    }, [phase, updateDraft]);

    const startDraft = useCallback(
        (span: BarSpan) => {
            updateDraft(span);
            setDraftUnit(unit);
            setPhase("dragging");
        },
        [unit, updateDraft],
    );
    const settle = useCallback(
        (span: BarSpan) => {
            updateDraft(span);
            setDraftUnit(unit);
            setPhase("settling");
        },
        [unit, updateDraft],
    );
    const resetDraft = useCallback(() => {
        updateDraft(null);
        setPhase("idle");
    }, [updateDraft]);

    const updatePreview = useCallback(
        (next: BarSpan | null) => {
            if (!isSameDraft(next, previewRef.current)) {
                previewRef.current = next;
                setPreview(next);
                onPreview?.(next);
            }
        },
        [onPreview],
    );

    const isCurrentUnit = draftUnit === unit;
    return {
        draft: isCurrentUnit ? draft : null,
        preview: isCurrentUnit ? preview : null,
        phase: isCurrentUnit ? phase : "idle",
        /** The draft as last set, in any unit, for event handlers. */
        draftRef,
        updateDraft,
        startDraft,
        settle,
        resetDraft,
        updatePreview,
    };
}

export type DragDraft = ReturnType<typeof useDragDraft>;
